import classNames from "classnames";
import { Entry } from "contentful";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";

import IconFilters from "@/icons/icon-filters";
import Dialog from "~/components/common/dialog";
import Grid from "~/components/common/grid";
import Icon from "~/components/common/icon";
import Checkbox from "~/components/form/checkbox";
import InputRadioCheckbox from "~/components/form/input-radio-checkbox";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";

import buttonStyles from "../../common/button.module.scss";
import editorialCardsStyles from "./editorial-cards.module.scss";
import styles from "./filter-cards.mobile.module.scss";

type Props = {
  cardFilter: Editorials.EditorialCards["cardFilter"];
  disableFirstBoxOfCardFilter?: boolean;
  activeCards: Entry<Editorials.Card>[];
  quantityFilters: Record<string, number>;
  activeFilters: string[];
  scrollUp: boolean;
  control: Control<any>;
  handleReset: () => void;
  handlePartialReset: (fields: string[]) => void;
};

export default function FilterCardsMobile({
  cardFilter,
  disableFirstBoxOfCardFilter,
  activeCards,
  activeFilters,
  scrollUp,
  control,
  handleReset,
  handlePartialReset,
  quantityFilters,
}: Props) {
  const filterAllTag = Constants.FILTER_CARDS_STANDARD.all;
  const filterEditorialTag = Constants.FILTER_CARDS_STANDARD.editorial;
  const filterVideoTag = Constants.FILTER_CARDS_STANDARD.video;
  const filterTutorialTypeTag = Constants.FILTER_CARDS_STANDARD.tutorialType;

  const numberFiltersWithoutFilterAll = activeFilters.filter((activeFilter) => activeFilter !== filterAllTag).length;

  const t = useTranslations();

  const [open, setOpen] = useState(false);

  // remove from filter the radio condition ("all" or "video")
  const otherFilters = useMemo(
    () => activeFilters.filter((f) => f !== filterAllTag && f !== filterVideoTag),
    [activeFilters, filterAllTag, filterVideoTag]
  );

  // number of "all" cards by applying filters
  const allFilteredCount = useMemo(() => {
    return activeCards.filter((card) => {
      const tags = card.metadata.tags.map((t) => t.sys.id).concat(filterAllTag, filterEditorialTag);
      return otherFilters.every((f) => tags.includes(f));
    }).length;
  }, [activeCards, otherFilters, filterAllTag, filterEditorialTag]);

  return (
    <>
      <div
        className={classNames(
          styles.filterBar,
          disableFirstBoxOfCardFilter ? styles.filterBarOnlyFilterButton : undefined,
          scrollUp ? styles.scrollUp : undefined
        )}
      >
        {!disableFirstBoxOfCardFilter && (
          <div className={styles.wrapperTutorialType}>
            <div className={styles.wrapperRadio}>
              <Controller
                name={filterTutorialTypeTag}
                control={control}
                render={({ field }) => (
                  <InputRadioCheckbox {...field} type="radio" value="all" checked={field.value === "all"}>
                    {t("generic.all")}
                  </InputRadioCheckbox>
                )}
              />
              {<span>{`(${allFilteredCount})`}</span>}
            </div>
            {quantityFilters[filterVideoTag] && (
              <div className={styles.wrapperRadio}>
                <Controller
                  name={filterTutorialTypeTag}
                  control={control}
                  render={({ field }) => (
                    <InputRadioCheckbox {...field} type="radio" value="video" checked={field.value === "video"}>
                      {t("generic.video")}
                    </InputRadioCheckbox>
                  )}
                />
                <span>{`(${quantityFilters[filterVideoTag]})`}</span>
              </div>
            )}
          </div>
        )}
        <button
          className={classNames(
            buttonStyles.button,
            disableFirstBoxOfCardFilter ? styles.onlyFilterButton : undefined,
            buttonStyles.secondary,
            styles.filterButton
          )}
          onClick={() => setOpen(true)}
        >
          {t("generic.filters")}
          {numberFiltersWithoutFilterAll > 0 && <span>{`(${numberFiltersWithoutFilterAll})`}</span>}
          <Icon icon={IconFilters} width={16} height={16} />
        </button>
      </div>
      <Dialog
        isOpen={open}
        title={
          <>
            <p>{t("generic.filters")}</p>
            {numberFiltersWithoutFilterAll > 0 && (
              <span className={editorialCardsStyles.highlightValue}>{numberFiltersWithoutFilterAll}</span>
            )}
          </>
        }
        customStyles={{ panel: styles.panel, content: styles.content, title: styles.title }}
        onClose={() => setOpen(false)}
      >
        {cardFilter &&
          cardFilter.length > 0 &&
          cardFilter.map((filter, i) => {
            const atLeastOneSelected = activeFilters.find((activeFilter) => filter.tags.includes(activeFilter));

            if (!filter.tags.find((tag) => quantityFilters[tag])) {
              return null;
            }

            return (
              <div key={filter.name + "-" + i} className={editorialCardsStyles.filterBox}>
                <div className={editorialCardsStyles.wrapperClearButton}>
                  <p className={editorialCardsStyles.filterBoxName}>{filter.name}</p>
                  <button
                    className={classNames(
                      editorialCardsStyles.clearButton,
                      atLeastOneSelected ? editorialCardsStyles.show : editorialCardsStyles.hide
                    )}
                    onClick={() => handlePartialReset(filter.tags)}
                  >
                    {t("generic.clear")}
                  </button>
                </div>
                {filter.tags.map((tag) => {
                  if (!quantityFilters[tag]) {
                    return null;
                  }

                  return (
                    <div key={tag} className={editorialCardsStyles.filterBoxCheck}>
                      <Controller
                        name={tag}
                        control={control}
                        render={({ field }) => (
                          <Checkbox {...field} label={t(`generic.${tag}`)} checked={activeFilters.includes(tag)} />
                        )}
                      />
                      <span>{`(${quantityFilters[tag]})`}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        {numberFiltersWithoutFilterAll > 0 && (
          <Grid className={styles.gridButtons}>
            <div className={styles.wrapperButtons}>
              <button type="button" className={styles.button} onClick={() => handleReset()}>
                {t("generic.clear_all")}
                <span className={editorialCardsStyles.highlightValue}>{numberFiltersWithoutFilterAll}</span>
              </button>
              <button type="button" className={styles.button} onClick={() => setOpen(false)}>
                {t("generic.view_results")}
                <span className={editorialCardsStyles.highlightValue}>
                  {
                    activeCards.filter((card) => {
                      const contentfulTags = card.metadata.tags;
                      let tags: string[] = [];

                      if (contentfulTags.find((tag) => tag.sys.id === filterVideoTag)) {
                        tags = contentfulTags.map((tag) => tag.sys.id).concat(filterAllTag);
                      } else {
                        tags = contentfulTags.map((tag) => tag.sys.id).concat([filterAllTag, filterEditorialTag]);
                      }

                      return activeFilters.every((filterId) => tags.includes(filterId));
                    }).length
                  }
                </span>
              </button>
            </div>
          </Grid>
        )}
      </Dialog>
    </>
  );
}
